import { faKey, Icon } from "@rivet-gg/icons";
import { useId } from "react";
import { Button, CopyTrigger, DiscreteInput, getConfig } from "@/components";
import { useEngineCompatDataProvider } from "@/components/actors";
import { Label } from "@/components/ui/label";
import { cloudEnv } from "@/lib/env";
import { features } from "@/lib/features";
import { useAdminToken, usePublishableToken } from "@/queries/accessors";

const needsManualPublishableToken = features.acl && !features.platform;

export function EnvVariables({
	id: _id,
	runnerName,
	endpoint,
	showRunnerName = true,
	showEndpoint = true,
	showPublicEndpoint = true,
	showCopyButton = true,
}: {
	id?: string;
	runnerName?: string;
	endpoint: string;
	showRunnerName?: boolean;
	showEndpoint?: boolean;
	showPublicEndpoint?: boolean;
	showCopyButton?: boolean;
}) {
	const rId = useId();
	const id = _id || rId;
	return (
		<div className="space-y-3">
			{showEndpoint && needsManualPublishableToken ? (
				<PublishableTokenNotice />
			) : null}
			<div
				className="gap-1 items-center grid grid-cols-2"
				data-env-variables={id}
			>
				<Label asChild className="text-muted-foreground text-xs mb-1">
					<p>Key</p>
				</Label>
				<Label asChild className="text-muted-foreground text-xs mb-1">
					<p>Value</p>
				</Label>
				{showPublicEndpoint && showEndpoint && <RivetPublicEndpointEnv endpoint={endpoint} />}
				{showEndpoint && <RivetRunnerEndpointEnv endpoint={endpoint} />}
				{showRunnerName && <RivetRunnerEnv runnerName={runnerName} />}
			</div>
			{showCopyButton ? (
				<div className="mt-2 flex justify-end">
					<CopyTrigger value={() => getEnvVariables({ id })}>
						<Button size="sm" variant="outline">
							Copy all raw
						</Button>
					</CopyTrigger>
				</div>
			) : null}
		</div>
	);
}

export function getEnvVariables({ id }: { id: string }) {
	const inputs = document.querySelectorAll<HTMLInputElement>(
		`[data-env-variables="${id}"] input`,
	);
	return Array.from(inputs)
		.reduce((acc, input, index) => {
			if (index % 2 === 0) {
				acc.push(`${input.value}=${inputs[index + 1]?.value}`);
			}
			return acc;
		}, [] as string[])
		.join("\n");
}

function RivetRunnerEnv({
	prefix,
	runnerName,
}: {
	prefix?: string;
	runnerName?: string;
}) {
	if (runnerName === "default" || !runnerName) return null;

	return (
		<>
			<DiscreteInput
				aria-label="environment variable key"
				value={`${prefix ? `${prefix}_` : ""}RUNNER_POOL`}
				show
			/>
			<DiscreteInput
				aria-label="environment variable value"
				value={runnerName || "default"}
				show
			/>
		</>
	);
}

export const useRivetDsn = ({
	endpoint,
	kind,
}: {
	endpoint?: string;
	kind: "publishable" | "secret";
}) => {
	const globalEndpoint = features.platform
		? cloudEnv().VITE_APP_API_URL
		: getConfig().apiUrl;

	// Publishable (RIVET_PUBLIC_ENDPOINT) always uses global endpoint.
	// Secret (RIVET_ENDPOINT) uses regional endpoint if provided.
	const apiEndpoint =
		kind === "publishable" ? globalEndpoint : endpoint || globalEndpoint;

	const dataProvider = useEngineCompatDataProvider();
	const publishableToken = usePublishableToken();
	const adminToken = useAdminToken();
	const namespace = dataProvider.engineNamespace;

	const auth =
		kind === "secret"
			? `${namespace}:${adminToken}`
			: !features.acl
				? namespace
				: `${namespace}:${publishableToken ?? "<PUBLISHABLE_TOKEN>"}`;

	const dsn = `https://${auth}@${apiEndpoint
		.replace("https://", "")
		.replace("http://", "")}`;

	return dsn;
};

export function RivetPublicEndpointEnv({
	prefix,
	endpoint,
}: {
	prefix?: string;
	endpoint?: string;
}) {
	const dsn = useRivetDsn({ endpoint, kind: "publishable" });
	return (
		<>
			<DiscreteInput
				aria-label="environment variable key"
				value={`${prefix ? `${prefix}_` : ""}RIVET_PUBLIC_ENDPOINT`}
				show
			/>
			<DiscreteInput
				aria-label="environment variable value"
				value={dsn}
				show
			/>
		</>
	);
}

export function PublishableTokenNotice() {
	return (
		<div className="flex gap-3 rounded-md border border-border bg-muted/30 p-3 text-sm">
			<Icon
				icon={faKey}
				className="mt-0.5 shrink-0 text-muted-foreground"
			/>
			<div className="space-y-1">
				<p className="font-medium">Publishable token required</p>
				<p className="text-muted-foreground text-xs">
					Replace{" "}
					<code className="rounded bg-muted px-1 py-0.5 text-foreground">
						{"<PUBLISHABLE_TOKEN>"}
					</code>{" "}
					in <code>RIVET_PUBLIC_ENDPOINT</code> with a token issued
					through your Rivet Enterprise RBAC configuration.
				</p>
			</div>
		</div>
	);
}

export function RivetRunnerEndpointEnv({
	prefix,
	endpoint,
}: {
	prefix?: string;
	endpoint?: string;
}) {
	const dsn = useRivetDsn({ endpoint, kind: "secret" });
	return (
		<>
			<DiscreteInput
				aria-label="environment variable key"
				value={`${prefix ? `${prefix}_` : ""}RIVET_ENDPOINT`}
				show
			/>
			<DiscreteInput
				aria-label="environment variable value"
				value={dsn}
				show
			/>
		</>
	);
}
